import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSession } from 'next-auth/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { bbox, multiPoint } from '@turf/turf';

import { useAppDispatch } from '@/core/store/store';
import { useAuthenticatedSession } from '@/core/hooks/useAuthenticatedSession';
import { ApiAccess, ReportSourceType } from '@/core/interfaces/common';
import { getCategories, getCountries, getCountriesWithWaters } from '@/core/store/actions/config';
import { getAllAppliedFiltersSelector } from '@/core/store/reducers/filtersSlice';
import { LoginStatesValues } from '@/core/constants/constants';
import {
  getCategoriesSelector,
  getCountriesWithWatersSelector,
} from '@/core/store/reducers/config';
import { MapContext } from '@/core/context/MapContext';

import { getUserSelector } from '@/features/Auth/store';
import { reportsPreferenceVersionSelector } from '@/features/UserPreferences/store/selectors';
import { getAnalysesList } from '@/features/ChatBot/store';
import { getFocusesList, getSharedFocusesList } from '@/features/Focus/store';
import {
  appendHorizonReports,
  appendLookoutReports,
  getHorizonReportsList,
  getLookoutReportsList,
  isReportsListLoadingSelector,
} from '@/features/Reports/store';
import { ReportResponseItem } from '@/features/Reports/interfaces';

import { transformAppliedFiltersToPostData } from '@/utils/filters';

export const GetInitialData = () => {
  const { mapRef } = useContext(MapContext);
  const fetchReportsInterval = useRef<NodeJS.Timeout | null>(null);
  const user = useSelector(getUserSelector);
  const reportsPreferenceVersion = useSelector(reportsPreferenceVersionSelector);
  const appliedFilters = useSelector(getAllAppliedFiltersSelector);
  const isReportsLoading = useSelector(isReportsListLoadingSelector);
  const dispatch = useAppDispatch();
  const { status } = useSession();
  const categories = useSelector(getCategoriesSelector);
  const countriesWithWaters = useSelector(getCountriesWithWatersSelector);

  useAuthenticatedSession(() => {
    if (!user) {
      return;
    }

    dispatch(getCategories());
    dispatch(getCountries());
    dispatch(getCountriesWithWaters());
  }, []);

  // Fetch focuses, shared focuses and analyses when we have categories and countries to map
  useEffect(() => {
    if (categories.length && countriesWithWaters.length) {
      dispatch(getFocusesList());
      dispatch(getSharedFocusesList());
      dispatch(getAnalysesList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, countriesWithWaters]);

  const hasHorizonFilter = useMemo(
    () =>
      appliedFilters.moreFilters?.some(filterItem => filterItem.value === ReportSourceType.HORIZON),
    [appliedFilters.moreFilters]
  );

  const hasLookoutFilter = useMemo(
    () =>
      appliedFilters.moreFilters?.some(filterItem => filterItem.value === ReportSourceType.LOOKOUT),
    [appliedFilters.moreFilters]
  );

  const shouldFetchHorizonReports = useMemo(
    () =>
      user?.organization.currentSubscription.apiAccess.includes(ApiAccess.HORIZON) &&
      (hasHorizonFilter || (!hasHorizonFilter && !hasLookoutFilter)),
    [hasHorizonFilter, hasLookoutFilter, user?.organization.currentSubscription.apiAccess]
  );

  const shouldFetchLookoutReports = useMemo(
    () =>
      user?.organization.currentSubscription.apiAccess.includes(ApiAccess.LOOKOUT) &&
      (hasLookoutFilter || (!hasHorizonFilter && !hasLookoutFilter)),
    [hasHorizonFilter, hasLookoutFilter, user?.organization.currentSubscription.apiAccess]
  );

  const reportFiltersData = useMemo(
    () => transformAppliedFiltersToPostData(appliedFilters),
    [appliedFilters]
  );

  useEffect(() => {
    const promisesList: Array<unknown> = [];

    if (status === LoginStatesValues.AUTHENTICATED && user) {
      if (shouldFetchHorizonReports) {
        promisesList.push(dispatch(getHorizonReportsList(reportFiltersData)));
      }

      if (shouldFetchLookoutReports) {
        promisesList.push(dispatch(getLookoutReportsList(reportFiltersData)));
      }
    }

    Promise.all(promisesList).then(fetchPromises => {
      // @ts-expect-error promise is a redux dispatch and `unwrapResult` works
      const results = fetchPromises.flatMap(unwrapResult) as Array<ReportResponseItem>;

      const reportsToFit = results
        .map(reportItem =>
          reportItem.location.map(reportLocation => [
            reportLocation.longitude,
            reportLocation.latitude,
          ])
        )
        .flat();

      if (reportsToFit.length) {
        const caluclatedBbox = bbox(multiPoint(reportsToFit));

        // @ts-expect-error BBox has correct return type
        mapRef?.getMap()?.fitBounds(caluclatedBbox, {
          padding: 50,
        });
      }
    });

    return () => {
      promisesList.forEach(promise => {
        // @ts-expect-error abort exists on Promise
        promise.abort();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, status, reportsPreferenceVersion]);

  const refetchNewReports = useCallback(() => {
    if (isReportsLoading) {
      return;
    }

    if (shouldFetchHorizonReports) {
      dispatch(
        getHorizonReportsList({
          ...reportFiltersData,
          page: 1,
        })
      )
        .unwrap()
        .then(response => {
          if (response.length > 0) {
            dispatch(appendHorizonReports(response));
          }
        });
    }

    if (shouldFetchLookoutReports) {
      dispatch(
        getLookoutReportsList({
          ...reportFiltersData,
          page: 1,
        })
      )
        .unwrap()
        .then(response => {
          if (response.length > 0) {
            dispatch(appendLookoutReports(response));
          }
        });
    }
  }, [
    dispatch,
    isReportsLoading,
    reportFiltersData,
    shouldFetchHorizonReports,
    shouldFetchLookoutReports,
  ]);

  useEffect(() => {
    fetchReportsInterval.current = setInterval(() => {
      refetchNewReports();
    }, 1000 * 30);

    return () => {
      if (fetchReportsInterval.current) {
        clearInterval(fetchReportsInterval.current);
      }
    };
  }, [refetchNewReports]);

  return null;
};
